/**
 * Tudo que a fonte sabe sobre a licitação — itens, arquivos e o registro cru.
 *
 * Fica só na página própria, não no painel lateral. O painel existe para
 * triar rápido percorrendo a lista, e um dossiê completo dentro de uma coluna
 * estreita atrapalha as duas coisas.
 *
 * A seção "Todos os campos" é deliberadamente burra: traduz as chaves que
 * conhece e mostra as demais como vieram. É o que faz um campo novo do PNCP
 * aparecer sozinho em vez de sumir por ninguém ter modelado — e é o que faz
 * este mesmo componente servir o Licitmais, cujo payload tem outro formato.
 */
import type { LicitacaoDetalhe } from "../api/cliente";
import { Secao } from "./Secao";
import painel from "../telas/painel.module.css";
import css from "./Dossie.module.css";

/** Rótulos das chaves conhecidas. Chave que não estiver aqui aparece crua —
 *  feia, mas visível, que é o ponto. */
const ROTULOS: Record<string, string> = {
  // PNCP
  amparoLegal: "Amparo legal",
  anoCompra: "Ano",
  dataAberturaProposta: "Abertura das propostas",
  dataAtualizacaoGlobal: "Última atualização",
  dataEncerramentoProposta: "Encerramento das propostas",
  dataInclusao: "Incluída em",
  dataPublicacaoPncp: "Publicada no PNCP",
  emendaParlamentar: "Emenda parlamentar",
  fontesOrcamentarias: "Fontes orçamentárias",
  informacaoComplementar: "Informação complementar",
  justificativaPresencial: "Justificativa da modalidade presencial",
  linkProcessoEletronico: "Processo eletrônico",
  linkSistemaOrigem: "Sistema de origem",
  modalidadeNome: "Modalidade",
  modoDisputaNome: "Modo de disputa",
  numeroCompra: "Número da compra",
  numeroControlePNCP: "Id da contratação no PNCP",
  objetoCompra: "Objeto",
  orgaoEntidade: "Órgão",
  orgaoSubRogado: "Órgão sub-rogado",
  processo: "Processo",
  sequencialCompra: "Sequencial",
  situacaoCompraNome: "Situação",
  srp: "Registro de preço",
  tipoInstrumentoConvocatorioNome: "Tipo de instrumento",
  unidadeOrgao: "Unidade compradora",
  unidadeSubRogada: "Unidade sub-rogada",
  usuarioNome: "Sistema que publicou",
  valorTotalEstimado: "Valor total estimado",
  valorTotalHomologado: "Valor total homologado",
  // Licitmais
  data: "Data",
  emails: "E-mails",
  fonte: "Fonte",
  modalidade: "Modalidade",
  municipioUf: "Município / UF",
  numero: "Número",
  objeto: "Objeto",
  obs: "Observações",
  orgao: "Órgão",
  palavrasChave: "Palavras-chave",
  urlsExternas: "Links externos",
  // Sebrae (portal SCF3): as chaves vêm em PascalCase, do ORM deles.
  DataHoraAberturaString: "Abertura",
  DataHoraAberturaSineDieString: "Abertura (sine die)",
  DataPublicacaoString: "Publicada em",
  DataEncerramentoString: "Encerrada em",
  LicitacaoModalidade: "Modalidade",
  Local: "Onde a disputa acontece",
  Numero: "Número",
  NumeroProcesso: "Processo",
  Objeto: "Objeto",
  SituacaoDescricao: "Situação",
  StatusDescricao: "Status no portal",
  TipoDescricao: "Critério",
  TipoJulgamentoDescricao: "Julgamento",
  ZSebrae: "Unidade do Sebrae",
};

/** O link que a pessoa abre. `pagina` quando a fonte a manda: o arquivo do
 *  Sebrae só se baixa em dois passos na mesma sessão, e o endereço gravado em
 *  `url` (esquema `sebrae+https`) nem o navegador abre — o detalhe do portal
 *  lista os arquivos com o botão de baixar dele. */
function linkDoArquivo(a: Record<string, unknown>): string {
  return String(a.pagina ?? a.url ?? "");
}

const moeda = (v: unknown) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : null;

/** Um valor de payload virando texto legível.
 *
 * `null` volta como null e a linha inteira some: campo vazio da fonte não é
 * informação, e trinta linhas de "—" escondem as que têm conteúdo. */
function texto(chave: string, v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  if (typeof v === "number")
    return /valor/i.test(chave) ? (moeda(v) ?? String(v)) : String(v);
  if (Array.isArray(v))
    return v.length ? v.map((x) => texto(chave, x) ?? "").join(" · ") : null;
  if (typeof v === "object") {
    // Os aninhados do PNCP (orgaoEntidade, unidadeOrgao, amparoLegal) são
    // rasos e têm um campo que resume o resto. Achatar em uma linha vale mais
    // que uma árvore para três chaves.
    const o = v as Record<string, unknown>;
    // `Descricao` é o do Sebrae ("SEBRAE MINAS GERAIS", "Concorrência").
    const resumo = o.nome ?? o.razaoSocial ?? o.nomeUnidade ?? o.descricao ?? o.Descricao;
    if (typeof resumo === "string") return resumo.trim();
    return Object.entries(o)
      .map(([k, x]) => `${ROTULOS[k] ?? k}: ${texto(k, x) ?? "—"}`)
      .join(" · ");
  }
  return String(v).trim() || null;
}

export function ItensDoDossie({ itens }: { itens: Record<string, unknown>[] }) {
  return (
    <Secao titulo="Itens" conta={itens.length}>
      <div className={css.rolagem}>
        <table className={painel.tabela}>
          <thead>
            <tr>
              <th className={css.colNum}>#</th>
              <th>Descrição</th>
              <th className={css.colQtd}>Qtd.</th>
              <th className={css.colUn}>Unidade</th>
              <th className={css.colValor}>Unitário</th>
              <th className={css.colValor}>Total</th>
              <th className={css.colCrit}>Julgamento</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((i, n) => (
              <tr key={String(i.numeroItem ?? n)}>
                <td>{String(i.numeroItem ?? n + 1)}</td>
                {/* O TEXTO E O TIPO SAO IRMAOS, e nao pai e filho. O corte
                    de linhas usa `-webkit-box` com `line-clamp`, e um filho
                    em `display: block` dentro dessa caixa quebra o corte: a
                    quinta linha aparecia pela metade, cortada no meio das
                    letras. Isolando o texto no proprio span, o corte volta a
                    terminar em reticencias e o tipo fica fora dele.

                    `title` com o texto inteiro porque o corte esconde
                    informacao de verdade — item de 400 caracteres e comum, e
                    quem precisa do resto nao deve ter de abrir o PDF. */}
                <th scope="row" className={css.celulaDescricao}>
                  <span className={css.descricao}>
                    {String(i.descricao ?? "—")}
                  </span>
                  {typeof i.materialOuServicoNome === "string" && (
                    <i className={css.tipo}>{i.materialOuServicoNome}</i>
                  )}
                </th>
                <td>{String(i.quantidade ?? "—")}</td>
                <td>{String(i.unidadeMedida ?? "—")}</td>
                <td>{moeda(i.valorUnitarioEstimado) ?? "—"}</td>
                <td>{moeda(i.valorTotal) ?? "—"}</td>
                <td>{String(i.criterioJulgamentoNome ?? "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Secao>
  );
}

export function ArquivosPublicados({ arquivos }: { arquivos: Record<string, unknown>[] }) {
  return (
    <Secao titulo="Arquivos publicados" conta={arquivos.length}>
      <ul className={css.arquivos}>
        {arquivos.map((a, n) => (
          <li key={String(a.url ?? a.idArquivo ?? n)}>
            <a href={linkDoArquivo(a)} target="_blank" rel="noopener noreferrer">
              {String(a.titulo ?? "arquivo sem título")}
            </a>
            <span className={css.meta}>
              {[
                a.tipoDocumentoNome,
                String(a.dataPublicacaoPncp ?? a.dataPublicacao ?? "").slice(0, 10),
                // O que o sistema não baixa (ata, homologação, além do teto)
                // continua na lista, com o motivo dito.
                a.fora_da_leitura ? `não lido: ${String(a.fora_da_leitura)}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </li>
        ))}
      </ul>
      {/* Não prometer o que o link não faz: ele abre na fonte, e o que foi
          lido do documento aparece na leitura do edital, não aqui. */}
      <p className={painel.rodape}>
        O link abre na fonte — no PNCP, o arquivo; no Sebrae, a página da
        licitação, onde o portal oferece o download.
      </p>
    </Secao>
  );
}

/** Os campos da fonte, um a um, ordenados. */
function camposDa(d: LicitacaoDetalhe) {
  return Object.entries(d.bruto)
    // `_bruto` e `_palavrasChave` são anotações nossas dentro do payload, não
    // dados da fonte. O `_` é a convenção que as marca.
    .filter(([k]) => !k.startsWith("_"))
    .map(([k, v]) => [ROTULOS[k] ?? k, texto(k, v)] as const)
    .filter(([, v]) => v !== null)
    .sort((a, b) => a[0].localeCompare(b[0], "pt-BR"));
}

/** Quantos campos a fonte mandou — para a aba dizer o número sem montar a lista. */
export const quantosCamposDaFonte = (d: LicitacaoDetalhe) => camposDa(d).length;

/** O aviso de que ninguém perguntou à fonte pelos itens e arquivos. */
export function DossiePendente({ d }: { d: LicitacaoDetalhe }) {
  if (d.dossie_em !== null) return null;
  return (
    <p className={painel.aviso}>
      Ninguém perguntou à fonte quais são os itens desta compra nem se há
      arquivo publicado. Rode <code>uv run nectuno-dossie</code>.
      {d.fonte === "Licitmais" && (
        <>
          {" "}
          No Licitmais isso não vai mudar: o portal não expõe essas listas.
        </>
      )}
    </p>
  );
}

/** O registro cru da fonte, numa área expansível própria. */
export function CamposDaFonte({ d }: { d: LicitacaoDetalhe }) {
  const campos = camposDa(d);
  if (!campos.length) return null;
  return (
    <details className={css.cru}>
      <summary>
        Todos os campos da fonte ({campos.length}) —{" "}
        {d.fonte === "PNCP" ? "registro do PNCP" : `registro do ${d.fonte}`}, como chegou
      </summary>
      <dl className={css.campos}>
        {campos.map(([k, v]) => (
          <div key={k} className={v!.length > 62 ? css.larga : ""}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/** O dossiê inteiro, como a página própria o mostrava antes das abas. */
export function Dossie({ d }: { d: LicitacaoDetalhe }) {
  return (
    <>
      {d.itens.length > 0 && <ItensDoDossie itens={d.itens} />}
      {d.arquivos.length > 0 && <ArquivosPublicados arquivos={d.arquivos} />}
      {d.dossie_em === null && (
        <Secao titulo="Itens e arquivos">
          <DossiePendente d={d} />
        </Secao>
      )}
      {camposDa(d).length > 0 && (
        <Secao titulo="Todos os campos da fonte" conta={camposDa(d).length}>
          <CamposDaFonte d={d} />
        </Secao>
      )}
    </>
  );
}
